/* global BigInt */
/* ============================================================================
   NM ANALYZER v4 — RULES ENGINE
   Deterministic. No AI. Same answers in, same report out, every time.
   Dr. Olutoyese Oyelese — Navigational Mind Architecture
   ========================================================================= */

const SECTIONS = [
 { id:'ground', n:'Ground & Anchors', goal:'What you are standing on, and what you are holding.',
   items:[
    {id:'ground.steady', reads:'Ground Status',      t:'Does the ground feel steady beneath you?', c:'no'},
    {id:'ground.structural', reads:'Structural Anchor',  t:'Is there something in your life that has not moved through this — something you can count on being the same?', c:'no'},
    {id:'ground.behavioural', reads:'Behavioural Anchor', t:'Have you been sleeping, eating and moving roughly as you normally do?', c:'no'},
    {id:'ground.emotional', reads:'Emotional Anchor',   t:'Is there one person whose being there tells you you are not alone?', c:'no'},
    {id:'ground.identity', reads:'Identity Anchor',    t:'Can you name something you have done that tells you who you are?', c:'no'}]},

 { id:'capacity', n:'Capacity & Load', goal:'What the house is spending against what it has.',
   items:[
    {id:'capacity.margin', reads:'Margin',           t:'Have you been getting to the end of most days exhausted?', c:'yes'},
    {id:'capacity.recovery', reads:'Recovery',         t:'Do you bounce back after a difficult task or situation?', c:'no'},
    {id:'capacity.breach', reads:'Ratio breach',     t:'Have you had to drop or postpone things?', c:'yes'},
    {id:'capacity.reassurance', reads:'Cost of reassurance',  t:'Are you tired even on days when nothing much has happened?', c:'yes'},
    {id:'capacity.spent', reads:'Spent',            t:'Do you feel spent?', c:'yes', sentinel:true}]},

 { id:'house', n:'The House', goal:'Which questions are live and which are going unanswered.', red:4,
   items:[
    {id:'house.trust', reads:'Trust reassurance', q:'Am I safe?',                        t:'Do you find yourself bracing for something to go wrong?', c:'yes'},
    {id:'house.autonomy', reads:'Autonomy',      q:'Do I choose this?',                 t:'Do you get to decide how your day goes?', c:'no'},
    {id:'house.initiative', reads:'Initiative',    q:'What can I try?',                   t:'Have you initiated anything recently?', c:'no'},
    {id:'house.industry', reads:'Industry',      q:'What must be done?',                t:'Do you complete most of your daily tasks?', c:'no'},
    {id:'house.identity', reads:'Identity',      q:'Who am I?',                         t:'Do you still feel like yourself?', c:'no'},
    {id:'house.intimacy', reads:'Intimacy',      q:'Can I be truly seen?',              t:'Are you in a close relationship with anyone?', c:'no'},
    {id:'house.generativity', reads:'Generativity',  q:'What am I building beyond myself?', t:'Are you building anything that will still be there without you?', c:'no'}]},

 { id:'alignment', n:'Alignment & Friction', goal:'Whether the house can produce a leader, or is at war.',
   items:[
    {id:'alignment.init_ind', reads:'Initiative ↔ Industry', d:'exploration vs completion',  t:'Do you find it hard to start anything?', c:'yes'},
    {id:'alignment.aut_id', reads:'Autonomy ↔ Identity',   d:'choosing vs being',          t:"Do you find yourself doing things that don't feel like you?", c:'yes'},
    {id:'alignment.tru_id', reads:'Trust ↔ Identity',      d:'safety vs self',             t:'Do you hold back from being yourself in order to stay safe?', c:'yes'},
    {id:'alignment.tru_ind', reads:'Trust ↔ Industry',      d:'safety vs necessity',        t:'Do most things feel too risky to start or do?', c:'yes'},
    {id:'alignment.tru_int', reads:'Trust ↔ Intimacy',      d:'safety vs being seen',       t:'Does letting people close feel unsafe?', c:'yes'},
    {id:'alignment.init_tru', reads:'Initiative ↔ Trust',    d:'trying vs safety',           t:'Do you avoid trying new things?', c:'yes'}]},

 { id:'loop', n:'The Loop', goal:'Whether the process runs, and whether it circles or spirals.',
   stem:'When you are performing a task:',
   items:[
    {id:'loop.sense', reads:'Sense',     t:'Do you notice when something changes?', c:'no'},
    {id:'loop.interpret', reads:'Interpret', t:'Can you make sense of what is happening?', c:'no'},
    {id:'loop.act', reads:'Act',       t:'Do you act on what you decide?', c:'no'},
    {id:'loop.reflect', reads:'Reflect',   t:'Do you look back at how it went?', c:'no'},
    {id:'loop.update', reads:'Update',    t:"Do you change your approach when something isn't working?", c:'no'},
    {id:'loop.orient', reads:'Orient',    t:'Do you find the task easier after you have done it once?', c:'no'}]},

 { id:'direction', n:'Direction', goal:'Whether there is a pull, how strong, at what cost.', 
   items:[
    {id:'direction.orientation', reads:'Orientation', t:'Do you feel like there is a way forward?', c:'no', sentinel:true},
    {id:'direction.vector', reads:'Vector',      t:"Do you feel pulled toward something, even if you can't name it?", c:'no'},
    {id:'direction.slope', reads:'Slope',       t:'Does moving feel hard right now?', c:'yes'}]},

 { id:'uncertainty', n:'Uncertainty', goal:'Whether movement is possible without complete information.',
   items:[
    {id:'uncertainty.certainty', reads:'Certainty requirement', t:'Do you need to be sure before you can act?', c:'yes'},
    {id:'uncertainty.tolerance', reads:'Tolerance',             t:'Can you sit with not knowing how something will turn out?', c:'no'},
    {id:'uncertainty.brake', reads:'The brake',             t:"Do you find yourself doing things you can't take back?", c:'yes'},
    {id:'uncertainty.closure', reads:'Premature closure',     t:"Do you push to settle things before you're ready?", c:'yes'}]},

 { id:'articulation', n:'Articulation', goal:'Whether what is happening can be named.', 
   items:[
    {id:'articulation.emotional', reads:'Emotional',   t:'Can you usually put your feelings into words?', c:'no', account:true},
    {id:'articulation.structural', reads:'Structural',  t:'Are you able to express what is going on in a way that someone else could understand?', c:'no', account:true},
    {id:'articulation.directional', reads:'Directional', t:'Can you describe the expectations that you have?', c:'no', account:true}]}
];

const ALL_ITEMS = SECTIONS.flatMap(s => s.items);
const TOTAL_ITEMS = ALL_ITEMS.length;            // 39

/* --- identity helpers: a section by name, and an item's own word ---------
   Positional lookups (SECTIONS[2]) break the moment a section is reordered. */
const SEC  = id => SECTIONS.find(s => s.id === id);
const key  = i  => i.id.split('.')[1];
const cap  = w  => w.charAt(0).toUpperCase() + w.slice(1);

/* --- R1 --------------------------------------------------------------- */
const isConcerning = (item, a) => a[item.id] !== undefined && a[item.id] === item.c;

/* --- R3: completeness. Decision 3 — block until every item is answered. */
function unanswered(a){ return ALL_ITEMS.filter(i => a[i.id] === undefined).map(i => i.id); }

/* --- R2: section status ------------------------------------------------
   Dr. Oyelese, 2026-09-27: "Yellow is a strong warning, red is critical and
   critical is EVERYthing being asked in that section is negative."

   GREEN   nothing concerning
   YELLOW  something concerning — a strong warning
   RED     every item in the section concerning — critical

   The House is the one section with a different bar: RED at four or more of
   its seven, because four of the seven questions going unanswered is already
   critical without waiting for all seven.

   This replaces a two-thirds proportional rule, which was mine and which made
   the effective bar wander between 67% and 80% by accident of section size. */
function sectionStatus(sec, a){
  const con = sec.items.filter(i => isConcerning(i, a));
  const red = sec.red ? con.length >= sec.red : con.length === sec.items.length;
  const status = con.length === 0 ? 'GREEN' : (red ? 'RED' : 'YELLOW');
  return { id:sec.id, name:sec.n, status, count:`${con.length}/${sec.items.length}`,
           concerning: con.map(i => i.reads),
           intact: sec.items.filter(i => !isConcerning(i, a)).map(i => i.reads) };
}

/* --- R8 sentinels ------------------------------------------------------ */
const sentinels = a => ALL_ITEMS.filter(i => i.sentinel && isConcerning(i, a))
                                .map(i => ({ id:i.id, reads:i.reads, text:i.t }));

/* The two axes, the route to spent (LOAD vs CIRCLING) and the four state
   classes were all removed on 2026-09-27. None of them is an NMF concept;
   each was mine. Dr. Oyelese: "The 2 axes is not an NMF concept — it was
   confabulated by you — delete it." The state classes likewise: "The four
   states do not exist in NMF."

   What survives is what the instrument actually reads: the eight section
   statuses, and the BOF output class derived from them. */

/* --- R11 anchors --------------------------------------------------------
   Which anchors are holding, and whether the ground is steady. Reported, not
   graded: the STRONG / AVAILABLE / LIMITED / CRITICAL_GAP scale came from
   v3.1, where it counted free-text boxes, and was never an NMF scale. The
   section status already says how serious the picture is. */
function anchors(a){
  const set = ['ground.structural','ground.behavioural','ground.emotional','ground.identity'].map(id => {
    const it = ALL_ITEMS.find(i => i.id === id);
    return { name: it.reads, holding: !isConcerning(it, a) };
  });
  return { set, holding: set.filter(x => x.holding).length,
           ground: isConcerning(ALL_ITEMS.find(i=>i.id==='ground.steady'), a) ? 'shifting' : 'steady' };
}

/* The four sections that carry the system. A RED in any of them is what
   Stabilization and REBUILD are for. */
const CARRYING = ['ground', 'capacity', 'house', 'loop'];

/* --- R7 phase ----------------------------------------------------------
   The phase pairs one-for-one with the output class. Dr. Oyelese, 2026-09-27:

     REBUILD        → Stabilization        a carrying section is RED
     HEDGED_VECTOR  → Soft Articulation    any RED
     HEDGED_VECTOR  → Direction Recovery   any YELLOW, no RED
     VECTOR         → Process Resumption   all GREEN

   More serious reads as an earlier phase: a RED anywhere outside the carrying
   four sends the person to Soft Articulation, and only a profile whose worst
   finding is YELLOW reaches Direction Recovery.

   Stabilization is for a system that cannot carry itself, not for any RED at
   all. A failed Direction with a working Loop is not a reason to stabilize:
   "you keep a functioning Loop from doing it's job — the loop with cognition
   makes Direction 'intentional'." A sentinel raises a flag and moves nothing. */
function phase(S){
  const v = Object.values(S).map(x => x.status);
  if (CARRYING.some(k => S[k].status === 'RED')) return 'STABILIZATION';
  if (v.includes('RED'))    return 'SOFT ARTICULATION';
  if (v.includes('YELLOW')) return 'DIRECTION RECOVERY';
  return 'PROCESS RESUMPTION';
}

/* --- R12 conditional annotations: fixed strings on stated triggers ----- */
function annotations(a, S){
  const C = id => isConcerning(ALL_ITEMS.find(i => i.id === id), a);
  const out = [];
  if (C('house.industry') === false && C('house.initiative'))
    out.push('Industry answered while Initiative is not — tasks completed, nothing started.');
  if (C('loop.reflect') && C('loop.update') && C('loop.orient'))
    out.push('The three stages that turn a circle into a spiral are the three not running.');
  if (C('loop.orient'))
    out.push('The Loop is capacity-conserving. A Loop that does not orient pays full price for ground already crossed.');
  if (C('ground.behavioural'))
    out.push('The Behavioural anchor is one of two that work when no Resident is online.');
  if (!C('direction.orientation') && C('direction.vector'))
    out.push('Orientation intact, Vector absent — a way forward exists, nothing is pulling toward it.');
  if (C('house.trust'))
    out.push('Trust is not malfunctioning; its resting position is alarm. What has failed is the reassurance.');
  /* Dr. Oyelese, 2026-09-27: "If the person enters spent but Capacity + Load is
     not full negative and loop is working this is a Yellow — Capacity is
     depleted and will become critical if nothing is done BUT capacity is NOT
     critical yet." A spent answer inside a YELLOW section is a trajectory
     warning, not a present emergency. */
  if (C('capacity.spent') && S.capacity.status !== 'RED')
    out.push('Spent is reported while Capacity & Load is not yet fully negative. Capacity is '
           + 'depleted and will become critical if nothing is done — but it is not critical yet.');
  if (C('capacity.reassurance'))
    out.push('Tired on days when nothing happened is the signature of reassurance being paid for rather than work.');
  if (S.alignment.status === 'RED' && S.uncertainty.status === 'RED')
    out.push('Alignment and Uncertainty are both RED — the house is in conflict and cannot act '
           + 'without being sure. A situation met from here may feel like a no-win. That is a '
           + 'reading of this person, not of the situation: nothing here establishes what the '
           + 'options cost or what anyone else requires, so it is not a tragic fork.');
  return out;
}

/* --- R12 summary: navigable / constraining ----------------------------- */
function summary(a){
  const nav = [], con = [];
  for (const s of SECTIONS) for (const i of s.items)
    (isConcerning(i, a) ? con : nav).push(`${s.n}: ${i.reads}`);
  return { nav, con };
}


/* --- BOF output class ---------------------------------------------------
   Dr. Oyelese, 2026-09-27:

     "Red at the Loop (all negative), Capacity and load (all negative), Ground
      and Anchors (all negative) and the house (4 or more negatives) = Rebuild,
      any red (anywhere) or Yellow in Listed areas (see red explanation) =
      Hedged_Vector else Vector"

   Four sections carry the system: Ground & Anchors, Capacity & Load, The House
   and The Loop. RED in any of those is REBUILD. RED anywhere else, or YELLOW
   in one of those four, is HEDGED_VECTOR.

   A sentinel raises the flag and nothing more. It does not change the class.

   TRAGIC_FORK is not produced here; see the note above bofClass's former fork
   branch. It remains canon in BOF and live for the AI Coordinator. */

function bofClass(S, sen){
  const flag = sen.length ? 'SAFETY_FLAG' : null;
  const carryingRed = CARRYING.some(k => S[k].status === 'RED');
  // VECTOR is all green and nothing less. Dr. Oyelese, 2026-09-27: "someone at
  // war cannot have all green." A yellow anywhere hedges the vector, whichever
  // section it sits in — yellow is a strong warning, and a warning unattended
  // is not full navigation capacity.
  const allGreen = Object.values(S).every(x => x.status === 'GREEN');

  if (carryingRed) return { cls:'REBUILD', flag,
      d:'Stabilization required before full navigation.' };
  if (!allGreen) return { cls:'HEDGED_VECTOR', flag,
      d:'Navigate with caution. Uncertainty preserved.' };
  return { cls:'VECTOR', flag, d:'Full navigation capacity. Direction available.' };
}

/* --- Demo scenarios: a clear baseline plus named overrides --------------- */
const CLEAR = () => { const a = {}; ALL_ITEMS.forEach(i => a[i.id] = (i.c === 'yes' ? 'no' : 'yes')); return a; };
const DEMOS = [
 { id:'navigating', n:'Navigating', d:'Everything answered. Baseline.', o:{} },
 { id:'unreassured', n:'Unreassured only', d:'Trust running, reassurance not holding. Capacity intact.',
   o:{'house.trust':'yes','alignment.tru_id':'yes','alignment.init_tru':'yes','direction.slope':'yes'} },
 { id:'load', n:'Depleted — Orient running', d:'Capacity nearly gone, the Loop still orienting.',
   o:{'capacity.margin':'yes','capacity.recovery':'no','capacity.breach':'yes','capacity.spent':'yes','ground.behavioural':'no'} },
 { id:'circling', n:'Depleted — circling', d:'Capacity nearly gone, and the Loop is not orienting.',
   o:{'capacity.margin':'yes','capacity.recovery':'no','capacity.breach':'yes','capacity.spent':'yes','loop.reflect':'no','loop.update':'no','loop.orient':'no'} },
 { id:'both', n:'Unreassured and depleted', d:'Trust unreassured and capacity nearly gone, on shifting ground. The worked case.',
   o:{'ground.steady':'no','ground.behavioural':'no','capacity.margin':'yes','capacity.recovery':'no','capacity.breach':'yes','capacity.reassurance':'yes','capacity.spent':'yes',
      'house.trust':'yes','house.initiative':'no','house.identity':'no','house.generativity':'no','alignment.init_ind':'yes','alignment.aut_id':'yes','alignment.tru_id':'yes','alignment.init_tru':'yes',
      'loop.reflect':'no','loop.update':'no','loop.orient':'no','direction.vector':'no','direction.slope':'yes','uncertainty.certainty':'yes','uncertainty.tolerance':'no','articulation.directional':'no'} },
 { id:'sentinel', n:'Sentinel — no way forward', d:'No way forward, with the Loop still running. Flagged, not stabilized.',
   o:{'capacity.margin':'yes','capacity.recovery':'no','capacity.breach':'yes','capacity.spent':'yes','loop.orient':'no','direction.orientation':'no','direction.vector':'no','direction.slope':'yes'} },
 { id:'quiet', n:'The quiet case', d:'Five sections green, Trust unreassured, capacity nearly gone. Would not register on a symptom checklist.',
   o:{'capacity.margin':'yes','capacity.recovery':'no','capacity.breach':'yes','capacity.reassurance':'yes','capacity.spent':'yes','house.trust':'yes','loop.orient':'no'} },
 { id:'conflicted', n:'At war, and needing certainty', d:'Alignment and Uncertainty both RED. Not a tragic fork — the instrument cannot read one.',
   o:{'alignment.init_ind':'yes','alignment.aut_id':'yes','alignment.tru_id':'yes','alignment.tru_ind':'yes','alignment.init_tru':'yes',
      'uncertainty.certainty':'yes','uncertainty.tolerance':'no','uncertainty.brake':'yes','articulation.directional':'no','direction.slope':'yes'} }
];
const demoAnswers = id => Object.assign(CLEAR(), (DEMOS.find(d => d.id === id) || {}).o || {});

/* ============================================================================
   ASSESSMENT CODE — the whole assessment as eight characters.

   Every item is binary, so 39 items are 39 bits, which is 8 characters in
   base36. The engine is deterministic, so those 39 bits regenerate the entire
   reading: section statuses, BOF class, sentinels, phase, anchors, house,
   anchors, house, friction, loop. A receiver does not copy the state — it runs
   the same computation on the same answers and arrives at the same result.

   The bit is the literal answer (yes = 1), not whether the answer was the
   concerning one. Storing the raw answer keeps an old code readable even if an
   item's concerning direction is ever revised.

   Format:  NMA4-XXXXXXXX-C
            |    |        `- check character, catches a mistyped code
            |    `---------- the 39 answers, in ALL_ITEMS order
            `--------------- the instrument version

   The version is not decoration. 39 bits mean something only against a known
   item order; a code from a different instrument would silently decode into a
   different profile. A receiver must REFUSE a version it does not hold.
   ========================================================================= */

const INSTRUMENT_VERSION = 'NMA4';
const CODE_CHARS = 8;

const checkChar = (body) => {
  let sum = 0;
  for (const ch of body) sum = (sum * 31 + parseInt(ch, 36)) % 36;
  return sum.toString(36).toUpperCase();
};

function encodeAnswers(a){
  const missing = unanswered(a);
  if (missing.length) throw new Error(`Cannot encode: ${missing.length} unanswered`);
  const bits = ALL_ITEMS.map(i => (a[i.id] === 'yes' ? '1' : '0')).join('');
  const body = BigInt('0b' + bits).toString(36).toUpperCase().padStart(CODE_CHARS, '0');
  return `${INSTRUMENT_VERSION}-${body}-${checkChar(body)}`;
}

function decodeAnswers(code){
  const cleaned = String(code || '').trim().toUpperCase().replace(/\s+/g, '');
  const m = cleaned.match(/^([A-Z0-9]+)-([A-Z0-9]{8})-([A-Z0-9])$/);
  if (!m) return { ok:false, error:'That is not an assessment code. Expected NMA4-XXXXXXXX-C.' };
  const [, version, body, check] = m;

  if (version !== INSTRUMENT_VERSION)
    return { ok:false, error:`This code is from instrument ${version}; this is ${INSTRUMENT_VERSION}. ` +
                            `The same bits mean different items in a different version, so it cannot be read here.` };
  if (checkChar(body) !== check)
    return { ok:false, error:'That code did not check out — a character is likely mistyped.' };

  // Built digit by digit: parseInt would lose precision above 2^53.
  let n = 0n;
  for (const ch of body) n = n * 36n + BigInt(parseInt(ch, 36));

  const bits = n.toString(2).padStart(ALL_ITEMS.length, '0');
  if (bits.length !== ALL_ITEMS.length)
    return { ok:false, error:'That code does not describe this instrument.' };

  const answers = {};
  ALL_ITEMS.forEach((item, k) => { answers[item.id] = bits[k] === '1' ? 'yes' : 'no'; });
  return { ok:true, version, answers };
}

/* --- assemble ---------------------------------------------------------- */
function analyse(a){
  const missing = unanswered(a);
  if (missing.length) return { complete:false, missing };
  const S = {}; SECTIONS.forEach(s => S[s.id] = sectionStatus(s, a));
  const sen = sentinels(a);
  return {
    complete:true, sections:S, sentinels:sen,
    bof: bofClass(S, sen),
    // Each carries an id as well as a label. The label is for the page; the id is
    // what a consumer switches on. `Trust reassurance` is what the item reads, `Trust`
    // is the station — the two are not interchangeable.
    house: SEC('house').items.map(i => ({ key: key(i), station: cap(key(i)),
             reads:i.reads, q:i.q, ok: !isConcerning(i, a) })),
    friction: SEC('alignment').items.map(i => ({ id:i.id, pair:i.reads, d:i.d,
             live: isConcerning(i, a) })),
    loop: SEC('loop').items.map(i => ({ key: key(i), stage:i.reads,
             running: !isConcerning(i, a) })),
    anchors: anchors(a),
    phase: phase(S),
    annotations: annotations(a, S),
    summary: summary(a),
    totals: { items: TOTAL_ITEMS, accounts: ALL_ITEMS.filter(i=>i.account).length }
  };
}

/* --- the Rebuild contract, defined once ---------------------------------
   Both sides build it from here: the Analyzer when it posts a live result,
   and Rebuild when it reads an assessment code. One definition means the two
   paths cannot drift, which is how `gateProfile` and `sectionProfile` came to
   disagree in the first place. */
const REBUILD_CONTRACT_VERSION = 'nm-analyzer/4.0';

function rebuildPayload(r, assessmentId, assessmentCode){
  return {
    contract: REBUILD_CONTRACT_VERSION,
    assessment_id: assessmentId,
    assessmentCode: assessmentCode || null,
    sectionProfile: Object.fromEntries(
      Object.entries(r.sections).map(([k, v]) => [k, v.status])),
    bofOutput: { class: r.bof.cls, flag: r.bof.flag },
    phase: r.phase,
    sentinels: r.sentinels.map(s => s.id),
    house: Object.fromEntries(r.house.map(h => [h.key, h.ok])),
    friction: r.friction.filter(f => f.live).map(f => ({ id: f.id, pair: f.pair })),
    loop: Object.fromEntries(r.loop.map(l => [l.key, l.running])),
    anchors: { holding: r.anchors.holding, ground: r.anchors.ground }
  };
}

/* Read a code and produce the contract. This is the whole of the transfer:
   decode 39 bits, run the engine, build the payload. Nothing is interpreted. */
function readAssessmentCode(code, assessmentId){
  const d = decodeAnswers(code);
  if (!d.ok) return { ok:false, error:d.error };
  const r = analyse(d.answers);
  if (!r.complete) return { ok:false, error:'That code did not produce a complete reading.' };
  return { ok:true, answers:d.answers, analysis:r,
           payload: rebuildPayload(r, assessmentId || Date.now(), code.trim().toUpperCase()) };
}

/* ============================================================================
   MOVEMENT

   Dr. Oyelese, 2026-09-26: "any change is movement so the real question is in
   what direction? ... anything trending towards normalcy is improvement,
   anything going the opposite way is worsening."

   Two questions, in order. Is there a change? And which way does it run?

   Every observable the instrument produces has an end that is normal, so
   direction is computable rather than interpreted.

   Nothing here is totalled. A profile that improves in one place and worsens in
   another is MIXED and stays mixed: variation lies in which parts moved, not in
   a net score. Nothing here establishes cause either — it says what changed
   between two readings, never what produced the change.
   ========================================================================= */

// Ordered worst → best. A higher index is nearer normal.
const SCALES = {
  status:       ['RED', 'YELLOW', 'GREEN'],
  // TRAGIC_FORK sits below REBUILD. Dr. Oyelese, 2026-09-26: "Tragic Fork does
  // not usually resolve but on the very very slim chance that it does, rebuild
  // or a hedged vector are probable outcomes." If those are its resolutions,
  // they are improvements from it, and it belongs at the foot of the scale
  // rather than off it.
  bof:          ['TRAGIC_FORK', 'REBUILD', 'HEDGED_VECTOR', 'VECTOR'],
  phase:        ['STABILIZATION', 'SOFT ARTICULATION', 'DIRECTION RECOVERY', 'PROCESS RESUMPTION'],
  ground:       ['shifting', 'steady']
};

const SECTION_LABEL = {
  ground:'Ground & Anchors', capacity:'Capacity & Load', house:'The House',
  alignment:'Alignment & Friction', loop:'The Loop', direction:'Direction',
  uncertainty:'Uncertainty', articulation:'Articulation'
};

const cap1 = w => w.charAt(0).toUpperCase() + w.slice(1);

function compareAssessments(prev, curr){
  if (!prev?.sectionProfile || !curr?.sectionProfile)
    return { ok:false, error:'Two assessments are needed before movement can be read.' };

  const improved = [], worsened = [], changed = [];
  let unchanged = 0;

  // scaled: position on an ordered scale
  const scaled = (what, scale, a, b) => {
    if (a === undefined || b === undefined || a === null || b === null) return;
    if (a === b) { unchanged++; return; }
    const s = SCALES[scale];
    const ia = s.indexOf(a), ib = s.indexOf(b);
    const row = { what, from:a, to:b };
    if (ia < 0 || ib < 0) changed.push(row);          // off-scale, e.g. TRAGIC_FORK
    else (ib > ia ? improved : worsened).push(row);
  };

  // flagged: a boolean where one value is the normal one
  const flagged = (what, a, b, normal, words) => {
    if (a === undefined || b === undefined) return;
    if (a === b) { unchanged++; return; }
    const row = { what, from: words[a ? 1 : 0], to: words[b ? 1 : 0] };
    (b === normal ? improved : worsened).push(row);
  };

  for (const k of Object.keys(SECTION_LABEL))
    scaled(SECTION_LABEL[k], 'status', prev.sectionProfile[k], curr.sectionProfile[k]);

  scaled('BOF output', 'bof', prev.bofOutput?.class, curr.bofOutput?.class);
  scaled('Phase',      'phase', prev.phase, curr.phase);
  scaled('Ground',     'ground', prev.anchors?.ground, curr.anchors?.ground);

  // anchors holding: a plain count, more is nearer normal
  if (prev.anchors && curr.anchors && prev.anchors.holding !== curr.anchors.holding) {
    const row = { what:'Anchors holding', from:`${prev.anchors.holding} of 4`, to:`${curr.anchors.holding} of 4` };
    (curr.anchors.holding > prev.anchors.holding ? improved : worsened).push(row);
  } else if (prev.anchors && curr.anchors) unchanged++;

  for (const k of Object.keys(curr.house || {}))
    flagged(`${cap1(k)} (house)`, prev.house?.[k], curr.house[k], true, ['unanswered','answered']);

  for (const k of Object.keys(curr.loop || {}))
    flagged(`${cap1(k)} (loop)`, prev.loop?.[k], curr.loop[k], true, ['not running','running']);

  // sentinels and friction: presence is the worse state
  const ids = x => new Set((x || []).map(f => (typeof f === 'string' ? f : f.id || f.pair)));
  const label = (x, id) => {
    const hit = (x || []).find(f => (typeof f === 'string' ? f : f.id) === id);
    return hit && typeof hit === 'object' ? hit.pair : id;
  };
  for (const [name, before, after, kind] of [
      ['Sentinel', ids(prev.sentinels), ids(curr.sentinels), 'sentinel'],
      ['Friction',  ids(prev.friction),  ids(curr.friction),  'friction']]) {
    for (const id of after) if (!before.has(id))
      worsened.push({ what:`${name} · ${label(curr[kind === 'sentinel' ? 'sentinels' : 'friction'], id)}`,
                      from:'not present', to:'present' });
    for (const id of before) if (!after.has(id))
      improved.push({ what:`${name} · ${label(prev[kind === 'sentinel' ? 'sentinels' : 'friction'], id)}`,
                      from:'present', to:'not present' });
    for (const id of after) if (before.has(id)) unchanged++;
  }


  // A mixed picture stays mixed. Nothing is totalled or netted off.
  const direction =
      (!improved.length && !worsened.length && !changed.length) ? 'NO CHANGE'
    : (improved.length && !worsened.length) ? 'IMPROVEMENT'
    : (worsened.length && !improved.length) ? 'WORSENING'
    : 'MIXED';

  const pi = SCALES.phase.indexOf(prev.phase), ci = SCALES.phase.indexOf(curr.phase);
  const phase = { from:prev.phase, to:curr.phase,
                  direction: (pi < 0 || ci < 0 || pi === ci) ? 'held'
                           : ci > pi ? 'advanced' : 'regressed' };

  return { ok:true, direction, improved, worsened, changed, unchanged, phase,
           moved: improved.length + worsened.length + changed.length };
}

export { SECTIONS, ALL_ITEMS, analyse, DEMOS, demoAnswers, sectionStatus, isConcerning,
         encodeAnswers, decodeAnswers, INSTRUMENT_VERSION, rebuildPayload, readAssessmentCode,
         REBUILD_CONTRACT_VERSION, compareAssessments };
